import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api';
import { TOUR_CONTENT } from '../../config/roleContent';
import Sidebar from './Sidebar';
import GuidedTour from '../ui/GuidedTour';

function isSidebarTourSelector(selector) {
  return typeof selector === 'string'
    && (
      selector.includes('[data-tour="nav-')
      || selector.includes('[data-tour="profile-card"]')
      || selector.includes('[data-tour="notifications"]')
    );
}

function buildTourSteps(user, isMobile) {
  const content = TOUR_CONTENT[user?.role] || TOUR_CONTENT.CA;

  // Helper to use shorter text for mobile to prevent huge cards
  const t = (full, mob) => (isMobile ? mob : full);

  const steps = [
    {
      title: 'Welcome to CA CRM',
      description: t(content.welcomeDescription, 'Manage your practice and clients effectively.'),
      placement: isMobile ? 'center' : 'auto',
    },
    {
      selector: '[data-tour="nav-dashboard"]',
      title: 'Dashboard module',
      description: t('Come back here anytime for totals, trends, recent activity, and shortcuts into the main CRM areas.', 'Access totals, trends, and recent updates.'),
      placement: isMobile ? 'bottom' : 'right',
    },
    {
      selector: '[data-tour="dashboard-hero"]',
      title: 'Dashboard overview',
      description: t(content.dashboardDescription, 'Summary of your practice activity.'),
      placement: 'bottom',
      scrollBlock: 'center',
    },
    {
      selector: '[data-tour="dashboard-theme-controls"]',
      title: 'Theme & Tour',
      description: t(content.themeControlsDescription, 'Change your colour theme or replay this tour.'),
      placement: 'bottom',
    },
  ];

  steps.push(
    {
      selector: '[data-tour="dashboard-filters"]',
      title: 'Smart filters',
      description: t(content.filtersDescription, 'Filter by manager, client, or date.'),
      placement: 'top',
    },
    {
      selector: '[data-tour="dashboard-metrics"]',
      title: 'Key metrics',
      description: t(content.metricsDescription, 'Track company and document totals.'),
      placement: 'top',
    },
    {
      selector: '[data-tour="dashboard-analytics"]',
      title: 'Analytics panels',
      description: t(content.analyticsDescription, 'Document and activity trends.'),
      placement: 'top',
    },
    {
      selector: '[data-tour="dashboard-attention"]',
      title: 'Action queue',
      description: t(content.attentionDescription, 'Items needing immediate focus.'),
      placement: 'top',
    },
    {
      selector: '[data-tour="dashboard-activity"]',
      title: 'Activity timeline',
      description: t(content.activityDescription, 'Latest platform activity.'),
      placement: 'top',
    },
    {
      selector: '[data-tour="dashboard-compliance"]',
      title: 'Document snapshot',
      description: t(content.complianceDescription, 'Document status by category.'),
      placement: 'top',
    }
  );

  steps.push(
    {
      selector: '[data-tour="nav-users"]',
      title: content.usersTitle,
      description: t(content.usersDescription, 'Manage team and client profiles.'),
      placement: isMobile ? 'bottom' : 'right',
    },
    {
      selector: '[data-tour="nav-companies"]',
      title: 'Companies module',
      description: t(content.companiesDescription, 'Client business and entity records.'),
      placement: isMobile ? 'bottom' : 'right',
    },
    {
      selector: '[data-tour="nav-documents"]',
      title: 'Documents module',
      description: t(content.documentsDescription, 'Review compliance files.'),
      placement: isMobile ? 'bottom' : 'right',
    },
    {
      selector: '[data-tour="nav-comments"]',
      title: 'Comments module',
      description: t(content.commentsDescription, 'Track discussion threads.'),
      placement: isMobile ? 'bottom' : 'right',
    }
  );

  if (user?.role === 'CA' || user?.role === 'AM') {
    steps.push({
      selector: '[data-tour="nav-assigned tasks"]',
      title: user?.role === 'CA' ? 'Assign Task' : 'My Tasks',
      description: t(content.tasksDescription, 'Manage and monitor task progress.'),
      placement: isMobile ? 'bottom' : 'right',
    });
  }

  if (user?.role === 'CA') {
    steps.push({
      selector: '[data-tour="nav-permissions"]',
      title: 'Permissions module',
      description: t(content.permissionsDescription, 'Manage manager access and roles.'),
      placement: isMobile ? 'bottom' : 'right',
    });
  }

  steps.push(
    {
      selector: '[data-tour="notifications"]',
      title: 'Notification Center',
      description: t(content.notificationsDescription, 'Task and activity updates.'),
      placement: isMobile ? 'bottom' : 'right',
    },
    {
      selector: '[data-tour="profile-card"]',
      title: 'Profile and settings',
      description: t(content.profileDescription, 'Profile and theme preferences.'),
      placement: isMobile ? 'bottom' : 'right',
    }
  );

  return steps;
}

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '3px solid var(--border)',
          borderTopColor: 'var(--brand)',
          animation: 'page-spin 0.65s linear infinite',
        }} />
        <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>Loading…</div>
      </div>
      <style>{`@keyframes page-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function AppLayout() {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 980 : false));
  const [tourOpen, setTourOpen] = useState(false);
  const [tourRequested, setTourRequested] = useState(false);
  const [activeTourStep, setActiveTourStep] = useState(null);
  const [tourSaving, setTourSaving] = useState(false);

  const isDashboardRoute = location.pathname === '/dashboard';
  const isTaskWorkspace = location.pathname.startsWith('/tasks/') && location.pathname !== '/tasks';
  const isChatRoute = location.pathname === '/chat';
  const tourSteps = useMemo(() => buildTourSteps(user, isMobile), [user, isMobile]);
  const forceSidebarOpen = Boolean(isMobile && tourOpen && isSidebarTourSelector(activeTourStep?.selector));

  const closeTourUi = useCallback(() => {
    setTourOpen(false);
    setTourRequested(false);
    setActiveTourStep(null);
  }, []);

  const markTourCompleted = useCallback(async () => {
    if (user?.is_tour_completed || tourSaving) {
      closeTourUi();
      return;
    }

    setTourSaving(true);
    try {
      await authAPI.completeTour();
      updateUser({ is_tour_completed: true });
    } catch (_) {
      // Keep the UI moving even if the request fails; the user can retry from the manual tour button.
    } finally {
      setTourSaving(false);
      closeTourUi();
    }
  }, [closeTourUi, tourSaving, updateUser, user?.is_tour_completed]);

  const closeTour = useCallback(() => {
    markTourCompleted();
  }, [markTourCompleted]);

  const completeTour = useCallback(() => {
    markTourCompleted();
  }, [markTourCompleted]);

  const startTour = useCallback(() => {
    setTourRequested(true);
    if (!isDashboardRoute) {
      navigate('/dashboard');
      return;
    }

    setTourOpen(true);
  }, [isDashboardRoute, navigate]);

  useEffect(() => {
    if (!tourRequested || !isDashboardRoute) return;

    setTourOpen(true);
    setTourRequested(false);
  }, [isDashboardRoute, tourRequested]);

  useEffect(() => {
    if (!user || !isDashboardRoute) return;
    if (user.is_tour_completed) return;

    setTourOpen(true);
  }, [isDashboardRoute, user]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 980);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        minHeight: '100dvh',
        height: '100dvh',
        background: 'var(--bg-page-accent), var(--bg-page)',
        fontFamily: "'Lora', 'DM Sans', serif",
        color: 'var(--text-primary)',
        overflowX: 'hidden',
        overflowY: 'hidden',
      }}
    >
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 64,
            background: 'var(--topbar-bg)',
            backdropFilter: 'blur(10px)',
            borderBottom: 'var(--topbar-border)',
            zIndex: 10,
            pointerEvents: 'none',
          }}
        />
      )}
      <Sidebar onStartTour={startTour} forceMobileOpen={forceSidebarOpen} tourActive={tourOpen} />
      <main style={{ flex: 1, width: '100%', minWidth: 0, minHeight: 0, overflowY: isChatRoute ? 'hidden' : 'auto', overflowX: 'hidden', display: isChatRoute ? 'flex' : undefined, flexDirection: isChatRoute ? 'column' : undefined }}>
        <div
          style={{
            minHeight: isChatRoute ? undefined : '100%',
            height: isChatRoute ? '100%' : undefined,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            padding: isMobile ? (isDashboardRoute ? '88px 0 24px' : '88px 12px 24px') : (isTaskWorkspace || isDashboardRoute || isChatRoute ? 0 : '28px 30px 34px'),
            display: isChatRoute ? 'flex' : undefined,
            flexDirection: isChatRoute ? 'column' : undefined,
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Outlet context={{ startTour }} />
          </Suspense>
        </div>
      </main>
      <GuidedTour
        open={tourOpen && isDashboardRoute}
        steps={tourSteps}
        onClose={closeTour}
        onComplete={completeTour}
        isMobile={isMobile}
        onStepChange={setActiveTourStep}
      />
    </div>
  );
}
